import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Button as RsButton, Spinner } from "reactstrap";

/**
 * 全站統一按鈕（design system primitive）。
 * 封裝 Reactstrap Button，提供彈性 props；功能頁一律用這個，勿散寫 raw Button。
 *
 *   <Button variant="primary">儲存</Button>
 *   <Button variant="outline-secondary" icon="mdi-magnify">查詢</Button>
 *   <Button variant="danger" loading>刪除中</Button>
 *   <Button variant="link" iconRight icon="mdi-chevron-right">更多</Button>
 *   <Button variant="success" block>整列送出</Button>
 *
 * variant：primary/secondary/success/danger/warning/info/light/dark/link
 *          或 outline-<color>（如 outline-primary）。
 */
export const Button = ({
  variant = "primary",
  size,
  loading = false,
  icon,
  iconRight = false,
  block = false,
  disabled = false,
  className,
  children,
  ...rest
}) => {
  let color = variant;
  let outline = false;
  if (typeof variant === "string" && variant.startsWith("outline-")) {
    outline = true;
    color = variant.slice("outline-".length);
  }
  return (
    <RsButton
      color={color}
      outline={outline}
      size={size}
      disabled={disabled || loading}
      className={classnames(className, { "w-100": block })}
      {...rest}
    >
      {loading ? (
        <Spinner size="sm" className="me-1 align-middle" />
      ) : icon && !iconRight ? (
        <i className={`mdi ${icon} me-1 align-middle`} />
      ) : null}
      {children}
      {icon && iconRight && !loading ? (
        <i className={`mdi ${icon} ms-1 align-middle`} />
      ) : null}
    </RsButton>
  );
};
Button.propTypes = {
  variant: PropTypes.string,
  size: PropTypes.oneOf(["sm", "lg"]),
  loading: PropTypes.bool,
  icon: PropTypes.string,
  iconRight: PropTypes.bool,
  block: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

/**
 * 圖示按鈕（列表列操作常用），預設無邊框。
 *   <IconButton icon="mdi-pencil" title="編輯" onClick={...} />
 */
export const IconButton = ({ icon, variant = "light", size = "sm", title, className, ...rest }) => (
  <RsButton
    color={variant.replace("outline-", "")}
    outline={variant.startsWith("outline-")}
    size={size}
    title={title}
    className={classnames("btn-icon", className)}
    {...rest}
  >
    <i className={`mdi ${icon}`} />
  </RsButton>
);
IconButton.propTypes = {
  icon: PropTypes.string.isRequired,
  variant: PropTypes.string,
  size: PropTypes.oneOf(["sm", "lg"]),
  title: PropTypes.string,
  className: PropTypes.string,
};

export default Button;
